import Phaser from 'phaser';
import { COLORS, GAME_HEIGHT, GAME_WIDTH } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { music } from '../core/MusicSystem';
import { localMusic, type LocalMusicSnapshot } from '../core/LocalMusicPlayer';
import { TOWERS, TOWER_ORDER } from '../data/towers';

// Keep the manual legible without covering the mission CTA column when the
// browser is using a wide 16:9/ultrawide viewport.
const PANEL_W = 340;

type MusicDeck = {
  play: Phaser.GameObjects.Text;
  track: Phaser.GameObjects.Text;
  time: Phaser.GameObjects.Text;
  volume: Phaser.GameObjects.Text;
  shuffle: Phaser.GameObjects.Text;
  bar: Phaser.GameObjects.Graphics;
  volumeBar: Phaser.GameObjects.Graphics;
};

/**
 * Compact operator manual that slides from the right edge of every screen.
 * It owns the local playlist so the same player is available during missions,
 * in command, and on the menu.
 */
export class FieldManualPanel {
  private scene: Phaser.Scene;
  private root: Phaser.GameObjects.Container;
  private isOpen = false;
  private destroyed = false;
  private musicFileInput?: HTMLInputElement;
  private musicUnsubscribe?: () => void;
  private musicDeck?: MusicDeck;
  private musicPoll?: Phaser.Time.TimerEvent;
  private content!: Phaser.GameObjects.Container;
  private manualScroll = 0;
  private scrollMax = 0;
  private scrollBar?: Phaser.GameObjects.Graphics;
  private wheelHandler?: (pointer: Phaser.Input.Pointer, _objects: unknown, _dx: number, dy: number) => void;

  constructor(scene: Phaser.Scene, depth = 130) {
    this.scene = scene;
    this.root = scene.add.container(GAME_WIDTH, 0).setDepth(depth);

    const g = scene.add.graphics();
    g.fillStyle(0x0b1116, 0.98);
    g.fillRect(0, 0, PANEL_W, GAME_HEIGHT);
    g.fillStyle(COLORS.accent, 0.08);
    g.fillRect(0, 0, PANEL_W, 46);
    g.lineStyle(2, COLORS.accent, 0.7);
    g.lineBetween(0, 0, 0, GAME_HEIGHT);
    g.lineStyle(1, COLORS.panelEdge, 1);
    for (let y = 0; y < GAME_HEIGHT; y += 4) g.lineBetween(0, y, PANEL_W, y);
    const art = scene.textures.exists('ui-command-grid')
      ? scene.add.image(PANEL_W / 2, GAME_HEIGHT / 2, 'ui-command-grid').setDisplaySize(PANEL_W, GAME_HEIGHT).setAlpha(0.08)
      : undefined;

    const close = scene.add
      .text(PANEL_W - 22, 22, '×', {
        fontFamily: 'Consolas, monospace',
        fontSize: '20px',
        color: '#6a7a88',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    close.on('pointerover', () => close.setColor('#ff6a6a'));
    close.on('pointerout', () => close.setColor('#6a7a88'));
    close.on('pointerdown', () => {
      audio.click();
      this.close();
    });

    const title = scene.add.text(18, 14, 'MANUAL', {
      fontFamily: 'Consolas, monospace',
      fontSize: '16px',
      color: '#5ec8ff',
      letterSpacing: 2,
    });
    const sub = scene.add.text(18, 32, 'APOGEE HOLD · OPERATOR MANUAL', {
      fontFamily: 'Consolas, monospace',
      fontSize: '9px',
      color: '#4a5560',
    });
    this.root.add([g, ...(art ? [art] : []), close, title, sub]);

    this.content = scene.add.container(0, 0);
    const maskShape = scene.make.graphics({ x: GAME_WIDTH - PANEL_W + 8, y: 52 }, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(0, 0, PANEL_W - 16, 520);
    this.content.setMask(maskShape.createGeometryMask());
    this.root.add(this.content);

    const section = (y: number, heading: string, body: string): number => {
      const h = scene.add.text(18, y, heading, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#d4a24a',
        letterSpacing: 1,
      });
      const b = scene.add.text(18, y + 18, body, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#9fb2c0',
        lineSpacing: 3,
        wordWrap: { width: PANEL_W - 36 },
      });
      this.content.add([h, b]);
      return y + 18 + b.height + 22;
    };

    let contentY = 62;
    contentY = section(contentY, 'OBJECTIVE', 'Hostiles leave a breach portal and follow the authored road to the command base. Intercept them before they breach integrity; surviving with more integrity earns more clear stars.');
    contentY = section(contentY, 'CONTROLS', [
      '1–9  build / select tower  ·  9 = SWARM HIVE',
      'SPACE  start or rush the next wave  ·  auto-waves advance',
      'Q / W / E / R  Spear · Barrier · Rolling Death · EMP',
      'T  switch Rolling Death mode  ·  F  fullscreen',
      'Click pad  place  ·  click tower  open upgrade menu',
      'Click the selected tower button again  cancel/drop placement',
      'U upgrade  ·  T priority  ·  S sell  ·  X evolve / tune',
      'B upgrade the base and unlock rooftop hardpoints',
      'H toggle this manual  ·  ESC open the system menu',
    ].join('\n'));
    contentY = section(contentY, 'COUNTERS & ECONOMY', [
      'BALLISTIC  →  light / fast targets',
      'THERMAL    →  swarms and regenerators',
      'EXPLOSIVE  →  tight groups',
      'ENERGY     →  shields and armour',
      'Bounties auto-harvest on death; rare caches favour elites and bosses.',
      'Keep supply for the next wave: upgrades and symbiosis refund 75% when swapping.',
    ].join('\n'));

    contentY = section(contentY, 'UPGRADE LADDER', 'Build → MK II → MK III → EXPERIMENTAL. At EXPERIMENTAL, choose an evolution or add a SYMBIOSIS module. Rank perks and research bonuses apply to live stats.');
    const upgradeRows = TOWER_ORDER.map((id) => {
      const def = TOWERS[id];
      const branches = def.evolvesTo?.map((evolution) => TOWERS[evolution.to]?.short ?? evolution.name).join(' / ') ?? '—';
      return `${def.short.padEnd(8, ' ')}  EXP → ${branches}`;
    }).join('\n');
    const upgradeText = scene.add.text(18, contentY, upgradeRows, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#b9c9d4',
      lineSpacing: 4,
    });
    this.content.add(upgradeText);
    contentY += upgradeText.height + 18;
    const baseText = scene.add.text(18, contentY, 'BASE  B  unlocks two rooftop hardpoints. Select the base itself to open its tooltip and upgrades.', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#6a7a88',
      wordWrap: { width: PANEL_W - 36 },
    });
    this.content.add(baseText);
    contentY += baseText.height + 24;
    contentY = section(contentY, 'MAP FORGE / DEV EDITOR', 'Enable DEV MODE in Pause → Gameplay, then press D from the mission screen. Drag the frosted alignment panel by its header, shape joined road nodes, lane spawns, the Bastion and pads. Right-click a node or empty terrain for insert, delete, 90° corner, placement and grid-snap controls. Pads snap to a 1–4 px precision grid and are rejected when they touch a road. Use the four cyan plane corners to fit the isometric projection to the terrain.');
    contentY = section(contentY, 'FIELDCRAFT', 'Route forks are intentional: cover merge points with splash, use focused fire on the long approach, and keep one emergency ability ready for bosses.');
    contentY = section(contentY, 'PROGRESSION', 'Mission clears award 15 DATA. Rank promotions grant 4 DATA; boss caches add more. DATA funds research for towers, evolutions, symbiosis modules and superweapons. Clear stars reward a perfect run, then remaining integrity.');
    contentY = section(contentY, 'READING THE FIELD', [
      'Red SPAWN gate = hostiles enter here. Cyan DEFEND gate + bastion = hold this.',
      'Bright centreline dashes show lane direction. Empty cyan pad rings accept builds.',
      'Red pad rings / ghost tint = invalid placement (occupied, locked, or off-pad).',
      'A leak flashes the screen edge and floats LEAK −N over the bastion.',
    ].join('\n'));
    contentY = section(contentY, 'READING THE HUD', 'Blue rings are build pads. Hidden caches are randomly assigned to some pads each sortie; build carefully and let luck reveal the reward. Wave signature icons show the actual hostile; hover one for its quick briefing. Desktop keyboard is the intended control scheme (see Pause → CONTROLS).');
    this.scrollMax = Math.max(0, contentY + 18 - 572);
    this.scrollBar = scene.add.graphics().setDepth(4);
    this.root.add(this.scrollBar);
    this.drawScrollBar();
    this.wheelHandler = (pointer, _objects, _dx, dy) => {
      if (!this.isOpen || pointer.x < GAME_WIDTH - PANEL_W || pointer.x > GAME_WIDTH || pointer.y < 52 || pointer.y > 572) return;
      this.manualScroll = Phaser.Math.Clamp(this.manualScroll + dy * 0.7, 0, this.scrollMax);
      this.content.setY(-this.manualScroll);
      this.drawScrollBar();
    };
    scene.input.on('wheel', this.wheelHandler);

    this.renderMusicDeck();
    this.musicUnsubscribe = localMusic.onChange((state) => this.refreshMusicDeck(state));
    this.musicPoll = scene.time.addEvent({ delay: 500, loop: true, callback: () => this.refreshMusicDeck(localMusic.snapshot()) });
    this.createMusicFileInput();

    // A scene can be rebuilt while changing command tabs. Release the browser
    // input and playlist listener with the panel instead of leaking them.
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());

    const tab = scene.add.container(-26, GAME_HEIGHT / 2);
    const tabBg = scene.add
      .rectangle(13, 0, 26, 108, 0x0e141a, 0.96)
      .setStrokeStyle(1.5, COLORS.accent)
      .setInteractive({ useHandCursor: true });
    const tabTxt = scene.add
      .text(13, 0, 'MANUAL', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#5ec8ff',
      })
      .setOrigin(0.5)
      .setAngle(90);
    tab.add([tabBg, tabTxt]);
    tabBg.on('pointerover', () => tabBg.setFillStyle(0x1a2830));
    tabBg.on('pointerout', () => tabBg.setFillStyle(0x0e141a));
    tabBg.on('pointerdown', () => this.toggle());
    this.root.add(tab);
  }

  /** Used by scene-level wheel handlers to avoid stealing manual scroll input. */
  isWheelTarget(pointer: Phaser.Input.Pointer): boolean {
    return this.isOpen && pointer.x >= GAME_WIDTH - PANEL_W && pointer.x <= GAME_WIDTH && pointer.y >= 52 && pointer.y <= 572;
  }

  private createMusicFileInput(): void {
    if (typeof document === 'undefined') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*,.aac,.flac,.m4a,.mp3,.ogg,.wav,.webm';
    input.multiple = true;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      if (input.files) localMusic.load(input.files);
      input.value = '';
    });
    document.body.appendChild(input);
    this.musicFileInput = input;
  }

  private renderMusicDeck(): void {
    const x = 18;
    const y = 582;
    this.root.add(this.scene.add.text(x, y, 'MUSIC PLAYER · LOCAL / SCORE', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#d4a24a',
      letterSpacing: 1,
    }));
    this.root.add(this.scene.add.text(x, y + 14, 'LOCAL FILES OR AUTHORED BATTLE SCORE  ·  SHUFFLE READY', {
      fontFamily: 'Consolas, monospace',
      fontSize: '8px',
      color: '#4a5560',
    }));

    const button = (bx: number, label: string, width: number, onClick: () => void) => {
      const bg = this.scene.add.rectangle(bx, 624, width, 24, 0x152028, 1)
        .setStrokeStyle(1, 0x2a3e4a)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      const txt = this.scene.add.text(bx, 624, label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#9ad4e8',
      }).setOrigin(0.5);
      bg.on('pointerover', () => bg.setFillStyle(0x1a2830));
      bg.on('pointerout', () => bg.setFillStyle(0x152028));
      bg.on('pointerdown', onClick);
      this.root.add([bg, txt]);
      return txt;
    };
    // Keep every action in one measured row.  The old hand-tuned x positions
    // let the two long labels collide at narrow panel widths.
    const hasLocal = () => localMusic.snapshot().tracks.length > 0;
    const controls: Array<[string, number, () => void]> = [
      ['◀', 26, () => { if (hasLocal()) localMusic.previous(); else music.nextSong(); audio.click(); }],
      ['PLAY', 52, () => { if (hasLocal()) localMusic.toggle(); else music.previewAuthoredTrack(0); audio.click(); }],
      ['▶', 26, () => { if (hasLocal()) localMusic.next(); else music.nextSong(); audio.click(); }],
      ['STOP', 44, () => { localMusic.stop(); music.stopBattleOpening(); audio.click(); }],
      ['SHUFFLE', 76, () => { if (hasLocal()) localMusic.toggleShuffle(); else music.toggleAuthoredShuffle(); audio.click(); }],
      ['ADD SONGS', 100, () => { this.musicFileInput?.click(); audio.click(); }],
    ];
    const gap = 4;
    const total = controls.reduce((sum, [, width]) => sum + width, 0) + gap * (controls.length - 1);
    let cursor = x + (PANEL_W - total) / 2;
    let play!: Phaser.GameObjects.Text;
    let shuffle!: Phaser.GameObjects.Text;
    for (const [label, width, onClick] of controls) {
      const txt = button(cursor + width / 2, label, width, onClick);
      if (label === 'PLAY') play = txt;
      if (label === 'SHUFFLE') shuffle = txt;
      cursor += width + gap;
    }

    const seekX = 18;
    const seekW = 352;
    const seekZone = this.scene.add.rectangle(seekX + seekW / 2, 658, seekW, 14, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    const setSeek = (pointer: Phaser.Input.Pointer) => {
      localMusic.seek(Phaser.Math.Clamp((pointer.x - seekX) / seekW, 0, 1));
    };
    seekZone.on('pointerdown', setSeek);
    seekZone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) setSeek(pointer);
    });
    const bar = this.scene.add.graphics();
    const track = this.scene.add.text(seekX, 644, 'NO LOCAL TRACKS — ADD SONGS TO BUILD A PLAYLIST', {
      fontFamily: 'Consolas, monospace',
      fontSize: '8px',
      color: '#6a7a88',
      wordWrap: { width: 352 },
    });
    const time = this.scene.add.text(18, 670, '0:00 / 0:00', {
      fontFamily: 'Consolas, monospace',
      fontSize: '8px',
      color: '#6a7a88',
    });
    const volume = this.scene.add.text(PANEL_W - 18, 670, 'VOL 100%  ·  MUSIC VOLUME', {
      fontFamily: 'Consolas, monospace',
      fontSize: '8px',
      color: '#6a7a88',
    }).setOrigin(1, 0);
    const volumeX = 190;
    const volumeW = 170;
    const volumeZone = this.scene.add.rectangle(volumeX + volumeW / 2, 693, volumeW, 16, 0x000000, 0.001)
      .setInteractive({ useHandCursor: true });
    const volumeBar = this.scene.add.graphics();
    const setVolume = (pointer: Phaser.Input.Pointer) => {
      const bounds = volumeZone.getBounds();
      localMusic.setVolume(Phaser.Math.Clamp((pointer.x - bounds.x) / bounds.width, 0, 1));
    };
    volumeZone.on('pointerdown', setVolume);
    volumeZone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) setVolume(pointer);
    });
    this.root.add([seekZone, bar, track, time, volume, volumeZone, volumeBar]);
    this.musicDeck = { play, track, time, volume, shuffle, bar, volumeBar };
    this.refreshMusicDeck(localMusic.snapshot());
  }

  private refreshMusicDeck(state: LocalMusicSnapshot): void {
    const deck = this.musicDeck;
    if (!deck) return;
    const current = state.tracks[state.index];
    const hasLocal = state.tracks.length > 0;
    deck.play.setText(hasLocal && state.playing ? 'PAUSE' : 'PLAY');
    deck.track.setText(current ? `LOCAL PLAYLIST ${state.index + 1}/${state.tracks.length}  ·  ${current}` : `SCORE · ${music.currentSongName}`);
    const fmt = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    };
    deck.time.setText(`${fmt(state.currentTime)} / ${fmt(state.duration)}`);
    deck.volume.setText(`VOL ${Math.round(state.volume * 100)}%  ·  MUSIC VOLUME`);
    const shuffleOn = hasLocal ? state.shuffle : music.isAuthoredShuffle;
    deck.shuffle.setText(shuffleOn ? 'SHUFFLE ON' : 'SHUFFLE OFF');
    deck.shuffle.setColor(shuffleOn ? '#7dce5a' : '#6a7a88');
    deck.bar.clear();
    deck.bar.fillStyle(0x152028, 1);
    deck.bar.fillRect(18, 655, 352, 5);
    deck.bar.fillStyle(COLORS.accent, 0.9);
    deck.bar.fillRect(18, 655, 352 * (state.duration ? state.currentTime / state.duration : 0), 5);
    deck.volumeBar.clear();
    deck.volumeBar.fillStyle(0x152028, 1);
    deck.volumeBar.fillRoundedRect(190, 690, 170, 5, 2);
    deck.volumeBar.fillStyle(COLORS.accent, 0.9);
    deck.volumeBar.fillRoundedRect(190, 690, 170 * state.volume, 5, 2);
  }

  private drawScrollBar(): void {
    const bar = this.scrollBar;
    if (!bar) return;
    bar.clear();
    bar.fillStyle(0x101820, 1);
    bar.fillRoundedRect(PANEL_W - 10, 58, 4, 504, 2);
    const thumbH = Math.max(56, 504 * (504 / (504 + this.scrollMax)));
    const thumbY = 58 + (504 - thumbH) * (this.manualScroll / Math.max(1, this.scrollMax));
    bar.fillStyle(COLORS.accent, 0.85);
    bar.fillRoundedRect(PANEL_W - 10, thumbY, 4, thumbH, 2);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.musicUnsubscribe?.();
    this.musicUnsubscribe = undefined;
    this.musicPoll?.remove(false);
    this.musicPoll = undefined;
    this.musicFileInput?.remove();
    this.musicFileInput = undefined;
    if (this.wheelHandler) this.scene.input.off('wheel', this.wheelHandler);
    this.wheelHandler = undefined;
    this.root.destroy(true);
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    if (this.isOpen || this.destroyed) return;
    this.isOpen = true;
    audio.click();
    this.scene.tweens.add({
      targets: this.root,
      x: GAME_WIDTH - PANEL_W,
      duration: 240,
      ease: 'Cubic.easeOut',
    });
  }

  close(): void {
    if (!this.isOpen || this.destroyed) return;
    this.isOpen = false;
    audio.click();
    this.scene.tweens.add({
      targets: this.root,
      x: GAME_WIDTH,
      duration: 200,
      ease: 'Cubic.easeIn',
    });
  }

  get opened(): boolean {
    return this.isOpen;
  }
}
